import React, {createRef, ReactElement, RefObject} from 'react';
import MComponent from '../Toolkit/Components/Component';
import {Column} from 'primereact/column';
import MTable, {MTableFilters} from './Toolkit/Table';
import MButton from '../Toolkit/Components/Controls/Button';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import {MNewIconName} from '../Toolkit/Icons';
import FileUploadDialog, {EditData, UploadData} from '../Dialogs/FileUploadDialog';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import {DataTableSortMeta} from 'primereact/datatable';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import {EditFileRequest, FileClient} from '../Api/FileClient';
import {FileData, FileMultiReply, FileReply} from '../Api/ControllerTypes/FileTypes';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../Api/TrafficManagementAccessTokenProvider';
import {IMsalContext} from '@azure/msal-react';
import {FilterMatchMode} from 'primereact/api';
import {MNullable} from '../Toolkit/Utils';
import EnumContext from '../Context/enum-context';
import MFormControl from '../Toolkit/Components/Controls/FormControl';
import MDropDown from '../Toolkit/Components/Controls/DropDown';

type FileRow = {
    data: FileData;
}

type Props = {
    parentId: string;
    selectionOnly: boolean;
    useEmailData?: boolean;
    onSelectionChange?: (newSelection: FileData[]) => void;
    msalContext: IMsalContext;
    updateEmails?: boolean;
}

/** */
type State = {
    filters: MTableFilters;
    selectedRows?: FileRow[] | FileRow | undefined;
    rowData: FileData[];
    showArchive: boolean;
}

class FileListControl extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>;

    private readonly m_fileClient: NSwagAdapter<FileClient>;
    private readonly m_uploadRef: RefObject<FileUploadDialog>;

    public constructor(props: Props) {
        super(props);

        this.m_fileClient = new NSwagAdapter<FileClient>(new FileClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_uploadRef = createRef<FileUploadDialog>();

        this.bindMethods();
        this.initializeState();
    }

    private bindMethods(): void {
        this.selectTemplate = this.selectTemplate.bind(this);
        this.isArchiveEnabled = this.isArchiveEnabled.bind(this);
        this.isRestoreEnabled = this.isRestoreEnabled.bind(this);
        this.isPlanner = this.isPlanner.bind(this);
        this.new_Click = this.new_Click.bind(this);
        this.edit_Click = this.edit_Click.bind(this);
        this.download_Click = this.download_Click.bind(this);
        this.archive_Click = this.archive_Click.bind(this);
        this.restore_Click = this.restore_Click.bind(this);
        this.toggleArchive_Click = this.toggleArchive_Click.bind(this);
        this.onUploadFile = this.onUploadFile.bind(this);
        this.onEditFile = this.onEditFile.bind(this);
        this.fileTypeTemplate = this.fileTypeTemplate.bind(this);
        this.fileStatusTemplate = this.fileStatusTemplate.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.fileFilter_Change = this.fileFilter_Change.bind(this);
    }

    private initializeState(): void {
        this.state = {
            filters: {},
            rowData: [],
            showArchive: false
        };
    }

    async componentDidMount() {
        await this.loadPageData();
    }

    private table_onSelectionChange(selection: FileRow | FileRow[] | undefined): void {

        if(this.props.selectionOnly) {
            if(this.props.onSelectionChange !== undefined) {
                if(selection !== undefined && (selection as FileRow[]).length > 0) {
                    this.props.onSelectionChange((selection as FileRow[]).map(s => s.data));
                    this.safeSetState(draft => draft.selectedRows = selection);
                } else {
                    this.props.onSelectionChange([]);
                }
            }
        } else {
            this.safeSetState((draft: State) =>
                draft.selectedRows = selection as FileRow | undefined);
        }

    }

    private isArchiveEnabled(): boolean {
        if(this.state.selectedRows !== undefined && !Array.isArray(this.state.selectedRows) && (this.state.selectedRows as FileRow).data !== undefined) {
            return this.state.selectedRows!.data!.fileStatus === 0;
        }

        return false;
    }

    private isRestoreEnabled(): boolean {
        if(this.state.selectedRows !== undefined && !Array.isArray(this.state.selectedRows) && (this.state.selectedRows as FileRow).data !== undefined) {
            return this.state.selectedRows!.data!.fileStatus === 1;
        }

        return false;
    }

    private isPlanner(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access;
    }

    private new_Click(): void {
        this.m_uploadRef.current!.show(this.onUploadFile);
    }

    private edit_Click(): void {
        this.m_uploadRef.current!.edit({
            id: (this.state.selectedRows as FileRow).data.id,
            fileType: (this.state.selectedRows as FileRow).data.fileType,
            fileFolder: (this.state.selectedRows as FileRow).data.fileFolder,
            note: (this.state.selectedRows as FileRow).data.note
        } ,this.onEditFile)
    }

    private async download_Click(): Promise<void> {
        const downloadReply = await this.m_fileClient.call(
            (client) => client.downloadFile((this.state.selectedRows as FileRow).data!.id));

        downloadReply.throwIfFailed();
    }

    private async archive_Click(): Promise<void> {
        const archiveReply = await this.m_fileClient.call(
            (client) => client.archiveFile((this.state.selectedRows as FileRow).data!.id));

        archiveReply.throwIfFailed();

        await this.loadPageData();
    }

    private async restore_Click(): Promise<void> {
        const restoreReply = await this.m_fileClient.call(
            (client) => client.restoreFile((this.state.selectedRows as FileRow).data!.id));

        restoreReply.throwIfFailed();

        await this.loadPageData();
    }

    private async toggleArchive_Click(): Promise<void> {
        this.safeSetState(s => s.showArchive = !s.showArchive);
    }

    private async select_Click(lineData: FileData): Promise<void> {
        const archiveReply = await this.m_fileClient.call(
            (client) => client.changeSendToCrew(lineData.id, !lineData.sendToCrew));

        archiveReply.throwIfFailed();

        await this.loadPageData();
    }

    private async fileFilter_Change(newValue: MNullable<number>): Promise<void> {
        this.safeSetState(draft =>
            draft.filters = {
                'data.fileType': {
                    value: newValue === -1 ? null : newValue!,
                    matchMode: FilterMatchMode.EQUALS
                }
            });
    }

    private async onUploadFile(data: UploadData) {
        const result = await this.m_fileClient.callWithReply(FileReply,
            (client) => client.uploadFile(this.props.parentId, data.fileType!, data.fileFolder!, data.file, data.note));
        result.throwIfFailed();

        //Refresh page before closing dialog.
        await this.loadPageData();
        this.m_uploadRef.current!.hide();
    }

    private async onEditFile(data: EditData) {
        const result = await this.m_fileClient.callWithReply(FileReply,
            (client) => client.editFile(new EditFileRequest({
                id: data.id,
                fileType: data.fileType,
                fileFolder: data.fileFolder,
                note: data.note
        })));
        result.throwIfFailed();

        //Refresh page before closing dialog.
        await this.loadPageData();
        this.m_uploadRef.current!.hide();
    }

    private async loadPageData(): Promise<void> {
        //Read file data.
        const fileResult = await this.m_fileClient.callWithReply(
            FileMultiReply, (client) => client.getAll(this.props.parentId));
        fileResult.throwIfFailed();

        this.safeSetState(draft => {
            draft.rowData = fileResult.reply!.files;
            if(this.props.useEmailData) {
                draft.selectedRows = fileResult.reply!.files.filter(cr => cr.sendToCrew).map(n => ({data: n}));
            }
        });
    }

    private tableSortTemplate(): DataTableSortMeta[] {
        return [
            {field: 'data.modifiedOn', order: -1}
        ];
    }

    private fileTypeTemplate(rowData: FileRow): string | null {
        const index = this.context.enums?.fileTypes.findIndex(value => value.id === rowData.data.fileType);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.fileTypes[index!].name!;
    }

    private fileStatusTemplate(rowData: FileRow): string | null {
        const index = this.context.enums?.fileStatuses.findIndex(value => value.id === rowData.data.fileStatus);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.fileStatuses[index!].name!;
    }

    private dateTemplate(rowData: FileRow): string | undefined {
        if(rowData.data.modifiedOn !== null) {
            try {
                return rowData.data.modifiedOn.toLocaleDateString();
            } catch {
                return '';
            }
        }
        return '';
    }

    private selectTemplate(rowData: FileRow): ReactElement {
        return <MButton label={rowData.data.sendToCrew ? 'Yes' : 'No'}
                        color={rowData.data.sendToCrew === true ? 'success' : 'primary'}
                        disabled={this.isPlanner}
                        onClick={this.select_Click.bind(this, rowData.data)}/>;
    }

    public render(): ReactElement {
        let fileList = this.state.rowData;
        if(!this.state.showArchive){
            fileList = fileList.filter(n => n.fileStatus !== 1);
        }

        const fileTypeRows = this.context.enums === null ? [] : this.context.enums!.fileTypes.map(folder => ({
            value: folder.id,
            label: folder.name
        }));

        return (
            <div>
                <FileUploadDialog ref={this.m_uploadRef}
                                  fileTypeList={this.context.enums?.fileTypes ?? []}
                                  fileFolderList={this.context.enums?.fileFolders ?? []}
                />
                {!this.props.selectionOnly && <MToolbar>
                    <MButton label="Add File"
                             color="success"
                             icon={MNewIconName}
                             disabled={false}
                             onClick={this.new_Click}/>
                    <MSpacer/>
                    <MButton label="Edit File"
                             icon={'pi pi-pencil'}
                             disabled={this.state.selectedRows === undefined || this.isPlanner}
                             onClick={this.edit_Click}/>
                    <MSpacer/>
                    <MButton label="Download"
                             icon={'pi pi-download'}
                             disabled={this.state.selectedRows === undefined}
                             onClick={this.download_Click}/>
                    <MSpacer/>
                    <MButton label="Archive"
                             icon={'pi pi-trash'}
                             color="danger"
                             disabled={!this.isArchiveEnabled() || this.isPlanner}
                             onClick={this.archive_Click}/>
                    <MSpacer/>
                    <MButton label={this.state.showArchive ? "Hide Archive" : "Show Archive"}
                             icon={this.state.showArchive ? 'pi pi-times' : 'pi pi-check'}
                             color="secondary"
                             disabled={false}
                             onClick={this.toggleArchive_Click || this.isPlanner}/>
                    <MSpacer/>
                    {this.state.showArchive && <MButton label="Restore"
                             icon={'pi pi-thumbs-up'}
                             color="secondary"
                             disabled={!this.isRestoreEnabled()}
                             onClick={this.restore_Click || this.isPlanner}/>}
                </MToolbar>}

                <div className="col-12 md:col-3" style={{marginTop: "2rem"}}>
                    <MFormControl label={''}
                                  id={'fileFolder'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MDropDown<number> id='fileFolderValue'
                                                   disabled={false}
                                                   items={fileTypeRows ?? []}
                                                   initialValue={-1}
                                                   onChange={this.fileFilter_Change}
                                />
                            );
                        }}
                    </MFormControl>
                </div>

                <MTable
                    rowKey="data.id"
                    rowData={fileList.map(n => ({data: n}))}
                    filters={this.state.filters}
                    multipleSelection={this.props.selectionOnly}
                    onSelectionChange={this.table_onSelectionChange}
                    selection={this.state.selectedRows}
                    sortMode={'multiple'}
                    multiSortMeta={this.tableSortTemplate()}>

                    <Column field="data.note"
                            header="File Notes"
                            filter={false}
                            sortable={false}
                            style={{width: this.props.selectionOnly? '75%' : '40%'}}/>
                    <Column field="data.fileType"
                            header="File Type"
                            filter={false}
                            sortable={true}
                            body={this.fileTypeTemplate}
                            style={{width: '10%'}}/>
                    {!this.props.selectionOnly && <Column header="E-mail"
                            body={this.selectTemplate}
                            filter={false}
                            sortable={false}
                            style={{width: '5%'}}/>}
                    {!this.props.selectionOnly && <Column field="data.fileStatus"
                            header="Status"
                            filter={false}
                            sortable={true}
                            body={this.fileStatusTemplate}
                            style={{width: '5%'}}/>}
                    {!this.props.selectionOnly && <Column field="data.modifiedBy"
                            header="Uploaded by"
                            filter={false}
                            sortable={false}
                            style={{width: '15%'}}/>}
                    {!this.props.selectionOnly && <Column field="data.modifiedOn"
                            header="File Date"
                            body={this.dateTemplate}
                            dataType="date"
                            filter={false}
                            sortable={false}
                            style={{width: '10%'}}/>}
                    <Column field="data.fileName"
                            header="File Name"
                            filter={false}
                            sortable={false}
                            style={{width: '15%'}}/>
                </MTable>
            </div>
        );
    }
}

export default FileListControl;